import { useRef, useEffect } from 'react';
import c3 from 'c3';
import type { KeyValuePoint } from '../../hooks/usePostProcessingMetrics';

interface Props {
  data: KeyValuePoint[];
  onRangeChange?: (start: Date, end: Date) => void;
  /** A point on the main chart was clicked — search that single date. */
  onDateClick?: (date: Date) => void;
}

export function DocumentTimeline({ data, onRangeChange, onDateClick }: Props) {
  const mainRef = useRef<HTMLDivElement>(null);
  const brushRef = useRef<HTMLDivElement>(null);
  const onRangeChangeRef = useRef(onRangeChange);
  onRangeChangeRef.current = onRangeChange;
  const onDateClickRef = useRef(onDateClick);
  onDateClickRef.current = onDateClick;

  useEffect(() => {
    if (!mainRef.current || !brushRef.current || !data.length) return;

    const xs = data.map(d => d.key);
    const counts = data.map(d => d.value);
    let brushing = false;

    const startDate = new Date(xs[0]);
    const endDate = new Date(xs[xs.length - 1]);
    const monthSpan = (endDate.getFullYear() - startDate.getFullYear()) * 12
      + (endDate.getMonth() - startDate.getMonth());

    const mainChart = c3.generate({
      bindto: mainRef.current,
      size: { height: 460 },
      data: {
        x: 'x',
        xFormat: '%Y-%m-%d',
        columns: [['x', ...xs], ['Document Count', ...counts]],
        types: { 'Document Count': 'area' },
        onclick: (e: { x: Date }) => onDateClickRef.current?.(new Date(e.x)),
      },
      padding: { left: 20, right: 20 },
      legend: { show: false },
      tooltip: {
        format: {
          title: (x: Date) =>
            new Date(x).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        },
      },
      axis: {
        x: {
          type: 'timeseries',
          tick: { format: '%b %Y', count: 10 },
        },
        y: {
          inner: true,
          tick: { format: (v: number) => v.toLocaleString() },
        },
      },
    });

    const brushChart = c3.generate({
      bindto: brushRef.current,
      size: { height: 50 },
      data: {
        x: 'x',
        xFormat: '%Y-%m-%d',
        columns: [['x', ...xs], ['Document Count', ...counts]],
        type: 'bar',
      },
      padding: { right: 20 },
      tooltip: { show: false },
      legend: { show: false },
      zoom: {
        enabled: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: 'drag' as any,
        disableDefaultBehavior: true,
        onzoomstart: () => {
          brushing = false;
          setTimeout(() => {
            if (!brushing) {
              mainChart.unzoom();
            } else {
              brushing = false;
            }
          }, 500);
        },
        onzoom: () => { brushing = true; },
        onzoomend: (domain: [Date, Date]) => {
          mainChart.zoom(domain);
          brushChart.unzoom();
          // c3 marks the drag-zoom group `disabled` when the drag ends, which c3.css
          // renders `visibility: hidden` — taking the selection rect and its handles with
          // it. Clear it so the chosen range stays highlighted in the brush bar.
          brushRef.current?.querySelector('.c3-drag-zoom')?.classList.remove('disabled');
          onRangeChangeRef.current?.(new Date(domain[0]), new Date(domain[1]));
        },
      },
      transition: { duration: 750 },
      axis: {
        x: {
          localtime: false,
          type: 'timeseries',
          tick: { format: '%Y', count: monthSpan, culling: { max: 14 } },
        },
        y: {
          min: Math.min(...counts),
          max: Math.min(...counts),
        },
      },
    });

    return () => {
      try { mainChart.destroy(); } catch (_) {}
      try { brushChart.destroy(); } catch (_) {}
    };
  }, [data]);

  return (
    <div>
      <div ref={mainRef} />
      <div className="brush" ref={brushRef} />
    </div>
  );
}
